import { convolveReal } from './fft.ts';
import { butterworthBandpass, filtfilt, usableOctaveBands, type OctaveBand } from './biquad.ts';
import { analyzeDecay, type DecayAnalysis } from './schroeder.ts';
import { computeRangeProfile, type RangeProfile, type RangeProfileOptions } from './rangeProfile.ts';
import { estimateRoom, type RoomEstimate, type RoomEstimateOptions } from './acoustics.ts';
import { findPeak, maxAbs, powerToDb, toFloat64 } from './util.ts';
import type { Sweep } from './sweep.ts';

export interface BandDecay {
  band: OctaveBand;
  rt60Sec: number;
  metric: string;
  r2: number;
  valid: boolean;
}

export interface MeasurementQuality {
  /** Peak of the impulse response - the acoustic gain of the whole path. */
  directLevel: number;
  /** How far the decay curve fell before reaching the noise floor. */
  usableDecayDb: number;
  /** Noise floor of the measurement, dB below the direct sound. */
  noiseFloorDb: number;
  /**
   * Energy well before the direct arrival, dB below it. In a deconvolved
   * up-sweep this region can only hold loudspeaker harmonic distortion, which
   * makes it a free distortion meter: above about -35 dB, turn the volume down.
   */
  distortionDb: number;
  /** True if the raw recording hit the rails. */
  clipped: boolean;
  warnings: string[];
}

export interface Measurement {
  sampleRate: number;
  sweep: Sweep;
  /** Band-limited impulse response, trimmed to start shortly before the direct sound. */
  ir: Float64Array;
  /** Index of the direct arrival within `ir`. */
  directIndex: number;
  directFractionalIndex: number;
  decay: DecayAnalysis;
  bands: BandDecay[];
  room: RoomEstimate;
  range: RangeProfile;
  quality: MeasurementQuality;
}

export interface AnalyzeOptions {
  temperatureC?: number;
  room?: RoomEstimateOptions;
  range?: Partial<Omit<RangeProfileOptions, 'directIndex'>>;
  /** How much of the deconvolution to keep ahead of the direct sound. */
  preRollSec?: number;
  /** Skip per-octave decay analysis (it is the slow part). */
  skipBands?: boolean;
}

/**
 * Recording plus known sweep, in; everything the app reports, out.
 *
 * Deliberately pure and synchronous: no audio context, no DOM, no clock. The
 * same function runs on a real capture, on a simulated room in the test suite,
 * and on a WAV file someone else recorded.
 */
export function analyzeMeasurement(
  recording: ArrayLike<number>,
  sweep: Sweep,
  options: AnalyzeOptions = {},
): Measurement {
  const sampleRate = sweep.spec.sampleRate;
  const preRollSec = options.preRollSec ?? 0.01;
  const warnings: string[] = [];

  const clipped = maxAbs(recording) >= 0.995;
  if (clipped) warnings.push('Recording clipped - lower the playback volume or move the phone away from the wall.');

  // Deconvolution proper: convolving with the inverse sweep collapses the
  // whole 1.2 s chirp back into one impulse, and the processing gain that
  // comes with it (10*log10(duration * bandwidth), about 43 dB here) is why
  // this works at all through a phone speaker in a room with a fridge running.
  const full = convolveReal(recording, sweep.inverse);

  // Restrict to where the sweep actually put energy. Outside that band the
  // deconvolution is dividing by nearly nothing, and it will happily amplify
  // traffic rumble or mains hum into an impulse response that is mostly a
  // slow wobble.
  const banded = filtfilt(full, butterworthBandpass(sweep.usableLowHz, sweep.usableHighHz, sampleRate, 4));

  const peak = findPeak(banded);
  const preRollSamples = Math.round(preRollSec * sampleRate);
  const irStart = Math.max(0, peak.index - preRollSamples);
  const ir = banded.slice(irStart);
  const directIndex = peak.index - irStart;
  const directFractionalIndex = peak.fractionalIndex - irStart;

  const distortionDb = measureDistortion(banded, peak, sweep);
  if (distortionDb > -25) {
    warnings.push('Strong harmonic distortion - the speaker is being driven too hard.');
  }

  const decay = analyzeDecay(ir, sampleRate, { startIndex: directIndex });
  if (decay.decayRangeDb > -35) {
    warnings.push('Not enough decay above the noise floor for a reliable RT60. Try a quieter moment.');
  }
  // Distortion products land ahead of the impulse but decay forward from
  // there. If the room rings for longer than that head start, an overdriven
  // speaker is feeding its own harmonics into the tail being measured.
  if (distortionDb > -40 && decay.best.rt60Sec > sweep.harmonicSeparationSec) {
    warnings.push(
      `This room rings for longer than the sweep separates distortion (${sweep.harmonicSeparationSec.toFixed(2)} s). ` +
        'Use a longer sweep or a lower volume.',
    );
  }

  const bands = options.skipBands
    ? []
    : usableOctaveBands(sampleRate, sweep.usableLowHz, sweep.usableHighHz).map((band) =>
        analyzeBand(ir, sampleRate, directIndex, band),
      );

  const room = estimateRoom(decay.best.rt60Sec, options.room);

  const range = computeRangeProfile(ir, sampleRate, {
    ...options.range,
    directIndex: directFractionalIndex,
    temperatureC: options.temperatureC ?? options.range?.temperatureC,
  });

  return {
    sampleRate,
    sweep,
    ir,
    directIndex,
    directFractionalIndex,
    decay,
    bands,
    room,
    range,
    quality: {
      directLevel: peak.magnitude,
      usableDecayDb: decay.decayRangeDb,
      noiseFloorDb: decay.noiseFloorDb,
      distortionDb,
      clipped,
      warnings,
    },
  };
}

function analyzeBand(
  ir: Float64Array,
  sampleRate: number,
  directIndex: number,
  band: OctaveBand,
): BandDecay {
  const filtered = filtfilt(ir, butterworthBandpass(band.lowHz, band.highHz, sampleRate, 4));
  try {
    const decay = analyzeDecay(filtered, sampleRate, { startIndex: directIndex });
    return {
      band,
      rt60Sec: decay.best.rt60Sec,
      metric: decay.best.metric,
      r2: decay.best.r2,
      valid: decay.best.valid,
    };
  } catch {
    return { band, rt60Sec: NaN, metric: 'T30', r2: 0, valid: false };
  }
}

/**
 * Harmonic distortion, read off the deconvolution ahead of the impulse.
 *
 * This is the trick that makes sweeping upward worth it. In an exponential
 * sweep the nth harmonic is the same sweep shifted earlier in time by
 * T*ln(n)/ln(f2/f1), so after deconvolution every distortion order collapses
 * into its own impulse at a known *negative* delay - the second harmonic
 * 190 ms ahead of the response, the third 300 ms ahead, and so on. Nothing
 * else can live there. So the region ahead of the direct sound is a free
 * distortion meter, and the same fact keeps those products out of the reverb
 * tail where they would otherwise inflate RT60.
 *
 * Measured at the predicted arrivals rather than across the whole pre-impulse
 * span: looking in the wrong place finds only the noise floor and reports a
 * badly overdriven speaker as clean.
 */
function measureDistortion(
  banded: Float64Array,
  peak: { index: number; magnitude: number },
  sweep: Sweep,
): number {
  const { sampleRate, durationSec, startHz, endHz } = sweep.spec;
  const sweepRate = Math.log(endHz / startHz);
  const halfWindow = Math.round(0.004 * sampleRate);

  let worst = 0;
  let measured = false;
  for (let harmonic = 2; harmonic <= 5; harmonic++) {
    const advanceSec = (durationSec * Math.log(harmonic)) / sweepRate;
    const centre = peak.index - Math.round(advanceSec * sampleRate);
    const from = Math.max(0, centre - halfWindow);
    const to = Math.min(banded.length, centre + halfWindow);
    if (to <= from || centre < 0) continue;
    measured = true;
    for (let i = from; i < to; i++) worst = Math.max(worst, Math.abs(banded[i]));
  }
  if (!measured) return -Infinity;
  return powerToDb(worst * worst, peak.magnitude * peak.magnitude);
}

export interface IrExportOptions {
  /** Silence kept ahead of the direct sound. */
  leadInSec?: number;
  /** Hard limit on exported length. */
  maxLengthSec?: number;
  /** Fade applied at the truncation point so the tail does not end in a click. */
  fadeOutSec?: number;
  /** Normalise the peak to this level, in dBFS. */
  peakDbfs?: number;
  /** Trim the tail where it sinks this far into the noise floor. */
  noiseMarginDb?: number;
}

/**
 * Trim, fade and normalise an impulse response for use as a convolution reverb.
 *
 * The tail is cut where the decay meets the noise floor, because a convolution
 * reverb loaded with 2 seconds of hiss sounds like 2 seconds of hiss. The cut
 * gets a fade so the tail does not end on a step.
 */
export function prepareIrForExport(measurement: Measurement, options: IrExportOptions = {}): Float64Array {
  const {
    leadInSec = 0.002,
    maxLengthSec = 10,
    fadeOutSec = 0.05,
    peakDbfs = -1,
    noiseMarginDb = 6,
  } = options;
  const { ir, sampleRate, directIndex, decay } = measurement;

  const start = Math.max(0, directIndex - Math.round(leadInSec * sampleRate));
  const decayEnd = directIndex + decay.truncationIndex;
  const noiseAllowance = Math.round((noiseMarginDb / 60) * Math.max(decay.best.rt60Sec || 0.5, 0.1) * sampleRate);
  const end = Math.min(
    ir.length,
    start + Math.round(maxLengthSec * sampleRate),
    decayEnd + noiseAllowance,
  );
  if (end <= start) return new Float64Array(0);

  const out = ir.slice(start, end);

  const fade = Math.min(out.length, Math.round(fadeOutSec * sampleRate));
  for (let i = 0; i < fade; i++) {
    const w = 0.5 - 0.5 * Math.cos((Math.PI * i) / fade);
    out[out.length - 1 - i] *= w;
  }

  const peak = maxAbs(out);
  if (peak > 0) {
    const target = Math.pow(10, peakDbfs / 20);
    const gain = target / peak;
    for (let i = 0; i < out.length; i++) out[i] *= gain;
  }
  return toFloat64(out);
}
